import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_ENDPOINT, Logger } from "@cede/utils";
import type { DirectOrderBookSnapshot, UseDirectOrderBookWSProps, UseDirectOrderBookWSResult } from "../types";
import { formatKatanaSymbol, toKatanaMarketSymbol, useKatanaWsConnection } from "@/hooks/exchanges/direct-ws/katana";
import type { KatanaSubscription } from "@/hooks/exchanges/direct-ws/katana";
import type { KatanaL2OrderBookData, KatanaL2OrderBookMessage } from "@/hooks/exchanges/direct-ws/katana/types";
import { clientConfig } from "@/lib/config/client-config";
import { safeDecimal } from "@/utils/hyperliquid/numeric";

const EXCHANGE_ID = "katana";
const REST_BASE = `${clientConfig.apiUrl}/${API_ENDPOINT.HUB_TRADE_ORDER_BOOK}`;
const TOP_LEVELS = 20;
const DEPTH_CHANNEL = "l2orderbook";

interface KatanaOrderBookSnapshot {
  sequence: number;
  bids: Map<number, number>;
  asks: Map<number, number>;
}

interface KatanaOrderBookSnapshotResponse {
  sequence?: number;
  bids?: [number, number][];
  asks?: [number, number][];
  timestamp?: number;
}

function applyDiff(book: KatanaOrderBookSnapshot, diff: KatanaL2OrderBookData) {
  for (const [price, quantity] of diff.b ?? []) {
    const priceNum = safeDecimal(price).toNumber();
    const quantityDec = safeDecimal(quantity);
    if (quantityDec.isZero()) book.bids.delete(priceNum);
    else book.bids.set(priceNum, quantityDec.toNumber());
  }
  for (const [price, quantity] of diff.a ?? []) {
    const priceNum = safeDecimal(price).toNumber();
    const quantityDec = safeDecimal(quantity);
    if (quantityDec.isZero()) book.asks.delete(priceNum);
    else book.asks.set(priceNum, quantityDec.toNumber());
  }
  book.sequence = diff.u;
}

function applySnapshot(book: KatanaOrderBookSnapshot, snapshot: KatanaOrderBookSnapshotResponse) {
  for (const [price, quantity] of snapshot.bids ?? []) {
    if (quantity === 0) continue;
    book.bids.set(price, quantity);
  }
  for (const [price, quantity] of snapshot.asks ?? []) {
    if (quantity === 0) continue;
    book.asks.set(price, quantity);
  }
}

function topBids(book: KatanaOrderBookSnapshot): [number, number][] {
  return Array.from(book.bids.entries())
    .sort((a, b) => b[0] - a[0])
    .slice(0, TOP_LEVELS);
}

function topAsks(book: KatanaOrderBookSnapshot): [number, number][] {
  return Array.from(book.asks.entries())
    .sort((a, b) => a[0] - b[0])
    .slice(0, TOP_LEVELS);
}

// Always query with the canonical Katana CCXT symbol `BASE/QUOTE:VBUSDC` rather
// than the raw `pairSymbol`. Upstream selection can carry a bare `KAT/USD` or a
// stale `:USD` suffix that no longer matches the SDK market key, throwing
// `MarketNotFoundError` from `exchange.market()`; `toKatanaMarketSymbol`
// re-attaches the protocol settlement token so the lookup always resolves.
async function fetchSnapshot(marketSymbol: string): Promise<KatanaOrderBookSnapshotResponse> {
  const params = new URLSearchParams({ exchangeId: EXCHANGE_ID, pairSymbol: marketSymbol });
  const response = await fetch(`${REST_BASE}?${params.toString()}`);
  if (!response.ok) throw new Error(`Katana orderbook snapshot HTTP ${response.status}`);
  return (await response.json()) as KatanaOrderBookSnapshotResponse;
}

/**
 * Subscribe to Katana `l2orderbook` diffs and apply them on top of the REST snapshot.
 * The wire subscribe/unsubscribe lifecycle is handled by `useKatanaWsConnection`
 * (ref-counted by subscription descriptor); this hook just supplies the
 * `id` + `subscription` and handles the REST-snapshot reconciliation:
 *   1. Subscribe (handled by the connection hook).
 *   2. Fetch the REST snapshot once the WS is connected.
 *   3. Buffer any diffs that arrive before the snapshot; on snapshot apply,
 *      discard buffered diffs with `sequence <= snapshot.sequence` per the
 *      Katana ordering recipe.
 */
export const useKatanaOrderBook = ({
  pairSymbol,
  enabled = true,
}: UseDirectOrderBookWSProps): UseDirectOrderBookWSResult => {
  const [orderBookData, setOrderBookData] = useState<DirectOrderBookSnapshot | null>(null);
  const [orderBookError, setOrderBookError] = useState<string | null>(null);

  const bookRef = useRef<KatanaOrderBookSnapshot | null>(null);
  const bufferRef = useRef<KatanaL2OrderBookData[]>([]);
  const expectedMarketRef = useRef<string>("");

  const market = pairSymbol ? formatKatanaSymbol(pairSymbol) : "";

  useEffect(() => {
    bookRef.current = null;
    bufferRef.current = [];
    expectedMarketRef.current = market;
    setOrderBookData(null);
    setOrderBookError(null);
  }, [market]);

  const emitTop = useCallback(() => {
    const book = bookRef.current;
    if (!book || !pairSymbol) return;
    setOrderBookData({
      timestamp: Date.now(),
      exchangeId: EXCHANGE_ID,
      pairSymbol,
      bids: topBids(book),
      asks: topAsks(book),
    });
    setOrderBookError(null);
  }, [pairSymbol]);

  const onMessage = useCallback(
    (msg: unknown) => {
      if (!market) return;

      const typed = msg as KatanaL2OrderBookMessage;
      if (typed.type !== DEPTH_CHANNEL || !typed.data || typed.data.m !== market) return;

      try {
        const book = bookRef.current;
        if (!book) {
          bufferRef.current.push(typed.data);
          return;
        }
        if (typed.data.u <= book.sequence) return;
        applyDiff(book, typed.data);
        emitTop();
      } catch {
        setOrderBookError("Failed to apply orderbook diff");
      }
    },
    [market, emitTop],
  );

  const subscription = useMemo<KatanaSubscription | undefined>(
    () => (market ? { name: DEPTH_CHANNEL, markets: [market] } : undefined),
    [market],
  );

  const { isConnected, error: connectionError } = useKatanaWsConnection({
    id: market ? `${DEPTH_CHANNEL}-${market}` : undefined,
    subscription,
    onMessage,
    enabled: enabled && !!pairSymbol,
  });

  // Fetch the REST snapshot once we're connected. Diffs arriving before the
  // snapshot are buffered by `onMessage` and replayed here.
  useEffect(() => {
    if (!isConnected || !pairSymbol || !market) return;
    let cancelled = false;

    fetchSnapshot(toKatanaMarketSymbol(pairSymbol))
      .then((snapshot) => {
        if (cancelled || expectedMarketRef.current !== market) return;
        const baseSequence = snapshot.sequence ?? 0;
        const book: KatanaOrderBookSnapshot = {
          sequence: baseSequence,
          bids: new Map(),
          asks: new Map(),
        };
        applySnapshot(book, snapshot);
        for (const buffered of bufferRef.current) {
          if (buffered.u > book.sequence) applyDiff(book, buffered);
        }
        bufferRef.current = [];
        bookRef.current = book;
        emitTop();
      })
      .catch((err) => {
        if (cancelled) return;
        Logger.error("[katana orderbook] snapshot fetch failed:", err);
        setOrderBookError(err instanceof Error ? err.message : "Failed to fetch orderbook snapshot");
      });

    return () => {
      cancelled = true;
    };
  }, [isConnected, pairSymbol, market, emitTop]);

  return {
    isConnected,
    orderBookData: orderBookData ?? undefined,
    error: orderBookError ?? connectionError,
  };
};
